import React from 'react';
import { getTenantAuditLogById, getTenantAuditLogs } from '../../../../../helpers/audit-log-helper';
import { getUserList } from '../../../../../helpers/user-helper';
import { buildAuditActorOptions } from '../helpers';
import { useAuditLogFilters } from './useAuditLogFilters';

export const useAuditLogListScreen = ({ token, role }) => {
    const filters = useAuditLogFilters();
    const deferredSearchTerm = React.useDeferredValue(filters.searchTerm);
    const [auditLogs, setAuditLogs] = React.useState([]);
    const [actorOptions, setActorOptions] = React.useState([{ value: 'ALL', label: 'Todos los actores' }]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [total, setTotal] = React.useState(0);
    const [hasNext, setHasNext] = React.useState(false);
    const [hasPrev, setHasPrev] = React.useState(false);
    const [selectedLog, setSelectedLog] = React.useState(null);
    const [isDetailOpen, setIsDetailOpen] = React.useState(false);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [detailError, setDetailError] = React.useState('');

    const queryParams = React.useMemo(() => {
        const params = {
            page: Math.floor(filters.offset / filters.limit) + 1,
            limit: filters.limit,
        };

        const normalizedSearch = String(deferredSearchTerm || '').trim();
        if (normalizedSearch) {
            params.search = normalizedSearch;
        }

        if (filters.entityType !== 'ALL') {
            params.entityType = filters.entityType;
        }

        if (filters.action !== 'ALL') {
            params.action = filters.action;
        }

        if (filters.actorUserId !== 'ALL') {
            params.actorUserId = filters.actorUserId;
        }

        if (filters.dateFrom) {
            params.dateFrom = filters.dateFrom;
        }

        if (filters.dateTo) {
            params.dateTo = filters.dateTo;
        }

        return params;
    }, [deferredSearchTerm, filters.offset, filters.limit, filters.entityType, filters.action, filters.actorUserId, filters.dateFrom, filters.dateTo]);

    const loadActorOptions = React.useCallback(async () => {
        if (!token || role !== 'ADMIN') {
            return;
        }

        const response = await getUserList(token, {
            limit: 500,
            offset: 0,
            includeDeleted: false,
        });

        if (!response?.success) {
            return;
        }

        setActorOptions(buildAuditActorOptions(response.data));
    }, [token, role]);

    const loadAuditLogs = React.useCallback(async () => {
        if (!token) {
            setError('No se pudo validar la sesión.');
            setAuditLogs([]);
            setIsLoading(false);
            return;
        }

        if (role !== 'ADMIN') {
            setError('Solo los administradores pueden consultar la auditoría.');
            setAuditLogs([]);
            setTotal(0);
            setHasNext(false);
            setHasPrev(false);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');

        const response = await getTenantAuditLogs(token, queryParams);
        if (!response?.success) {
            setError(response?.message || 'No se pudo obtener el historial de auditoría.');
            setAuditLogs([]);
            setTotal(0);
            setHasNext(false);
            setHasPrev(false);
            setIsLoading(false);
            return;
        }

        setAuditLogs(Array.isArray(response.data) ? response.data : []);
        setTotal(Number(response.total || 0));
        setHasNext(Boolean(response.hasNext));
        setHasPrev(Boolean(response.hasPrev));
        setIsLoading(false);
    }, [token, role, queryParams]);

    React.useEffect(() => {
        loadActorOptions();
    }, [loadActorOptions]);

    React.useEffect(() => {
        loadAuditLogs();
    }, [loadAuditLogs]);

    const openDetail = React.useCallback(async (auditLog) => {
        if (!auditLog?._id || !token) {
            return;
        }

        setIsDetailOpen(true);
        setDetailLoading(true);
        setDetailError('');
        setSelectedLog(auditLog);

        const response = await getTenantAuditLogById(token, auditLog._id);
        if (!response?.success) {
            setDetailError(response?.message || 'No se pudo obtener el detalle del registro.');
            setDetailLoading(false);
            return;
        }

        setSelectedLog(response.data || auditLog);
        setDetailLoading(false);
    }, [token]);

    const closeDetail = React.useCallback(() => {
        setIsDetailOpen(false);
        setDetailLoading(false);
        setDetailError('');
        setSelectedLog(null);
    }, []);

    return {
        ...filters,
        auditLogs,
        actorOptions,
        isLoading,
        error,
        total,
        hasNext,
        hasPrev,
        selectedLog,
        isDetailOpen,
        detailLoading,
        detailError,
        openDetail,
        closeDetail,
    };
};